import { RestRequest, RestContext, ResponseFunction } from 'msw'
import {
  addToEntityListData,
  updateEntityListData,
  updateEntityListDataMultiple,
  updateEntityListDataMultipleFromUpdatedEntityData,
  deleteEntityListData,
  deleteEntityListDataMultiple,
} from '../../modules/mockDataCommon'
import {
  findVersionConflict,
  versionConflictBody,
} from '../../modules/versionConflict'
import { requireAuthenticatedUser } from '../../decorators'
import { withSupplierProductAndItemGuard } from '../../modules/supplierProductAndItemConflict'
import { createMockOperation } from '../operations'
import {
  getCompanyMSServiceListData,
  getCompanyMSServiceDataById,
  getCompanyDataByCompanyMSServiceId,
  propagateCompanyMSServiceOrServicesAdditions,
  propagateCompanyMSServicesChanges,
  propagateCompanyMSServiceChanges,
  propagateCompanyMSServiceDeletions,
} from './mockData'
import type { ICompany, ICompanyMSService } from '~/types/company/models'

// async function getCompanyMSServices(
//   req: RestRequest,
//   res: ResponseFunction,
//   ctx: RestContext
// ) {
//   console.log(`'Returning companymasterservices.`)
//   return res(ctx.status(200), ctx.json({ items: getCompanyMSServiceListData() }))
// }

interface GetAllCompanyMSServicesResponse {
  data: ICompanyMSService[]
}

async function getAllCompanyMSServices(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  console.log(`'Returning all companyMSServices.`)
  const responseBody: GetAllCompanyMSServicesResponse = {
    data: await getCompanyMSServiceListData(),
  } // may be empty array. That's a valid response.
  console.log(responseBody)
  return res(ctx.status(200), ctx.json(responseBody))
}

interface GetCompanyMSServiceResponse {
  data: ICompanyMSService
}

/**
 * GET one companyMSService.
 *
 * The real API has had this all along -- it is part of the CRUD set
 * `make_request_handlers` generates for every entity -- and the mock simply
 * never grew it, because nothing in the webapp fetched a single msService by id
 * until the Case dialog needed to walk UP from the service it stores to the
 * service group and company that own it (`masterService.parent`). That is the
 * mock being NARROWER than the server, not the other way round -- but the mock
 * is WIDER in the other direction too, so check both: the
 * `/api/companymasterservices/:id/companymsservices` route in
 * ../companyMasterServices/index.ts is mock-only, with no server counterpart,
 * so don't reach for it.
 */
async function getCompanyMSService(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  try {
    const responseBody: GetCompanyMSServiceResponse = {
      data: await getCompanyMSServiceDataById(id), // throws when there is no such row
    }
    return res(ctx.status(200), ctx.json(responseBody))
  } catch (e: unknown) {
    // Narrowed, unlike the sibling handlers' bare `e.message` -- those are
    // TS18046s already counted in the ratchet baseline, and copying the idiom
    // would have raised it.
    const responseBody = {
      statusCode: 404,
      message: `Can't get companyMSService ${id}: ${
        e instanceof Error ? e.message : String(e)
      }`,
    }
    return res(ctx.status(404), ctx.json(responseBody))
  }
}

interface GetCompanyFromCompanyMSServiceResponse {
  data: ICompany
}

async function getCompanyFromCompanyMSService(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  try {
    const responseBody: GetCompanyFromCompanyMSServiceResponse = {
      data: await getCompanyDataByCompanyMSServiceId(id), // will either be valid or will have thrown error
    }
    return res(ctx.status(200), ctx.json(responseBody))
  } catch (e: unknown) {
    const responseBody = {
      statusCode: 404,
      message: `Can't get Company from companyMSService ${id}: ${e.message}`,
    }
    return res(ctx.status(404), ctx.json(responseBody))
  }
}

// post

const postCompanyMSService = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return addToEntityListData(
    req,
    res,
    ctx,
    getCompanyMSServiceListData,
    undefined,
    propagateCompanyMSServiceOrServicesAdditions
  )
}

// patch

const patchCompanyMSService = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return updateEntityListData(req, res, ctx, getCompanyMSServiceListData, [
    propagateCompanyMSServiceChanges,
  ])
}

const patchCompanyMSServices = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const idempotencyKey = req.headers.get('Idempotency-Key')
  if (idempotencyKey) {
    const updatedEntityDataMultiple = await req.json()
    // ⚠ THE VERSION CHECK HAS TO BE HERE TOO, and its absence is why every
    // multi-row msService PATCH in the mock accepted any version at all: the
    // check lives in `updateEntityListDataMultiple`, this branch calls the
    // underlying helper directly, and `update*MSService` always sets
    // `doAsync: true` for an array -- so this is the ONLY branch these requests
    // ever take. The 409 class the mock was fixed to reproduce was unreachable
    // on the one path the app actually uses.
    const conflict = findVersionConflict({
      listData: await getCompanyMSServiceListData(),
      submittedRows: updatedEntityDataMultiple,
    })
    if (conflict) {
      return res(ctx.status(409), ctx.json(versionConflictBody(conflict)))
    }
    const data = await updateEntityListDataMultipleFromUpdatedEntityData({
      req,
      updatedEntityDataMultiple,
      getEntityListDataFn: getCompanyMSServiceListData,
      postProcessOnceForAllFn: propagateCompanyMSServicesChanges,
    })
    const operationId = createMockOperation(
      'PATCH /api/companymsservices',
      data
    )
    return res(
      ctx.status(202),
      ctx.json({
        data: {
          operation_id: operationId,
          status: 'pending',
          operation_type: 'PATCH /api/companymsservices',
        },
      })
    )
  }
  return updateEntityListDataMultiple(
    req,
    res,
    ctx,
    getCompanyMSServiceListData,
    undefined,
    propagateCompanyMSServicesChanges
  )
}

// delete

const deleteCompanyMSService = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return deleteEntityListData(
    req,
    res,
    ctx,
    getCompanyMSServiceListData,
    undefined,
    propagateCompanyMSServiceDeletions
  )
}

const deleteCompanyMSServices = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return deleteEntityListDataMultiple(
    req,
    res,
    ctx,
    getCompanyMSServiceListData,
    undefined,
    propagateCompanyMSServiceDeletions // 'Service' is correctly singular here
  )
}

export const handlers = [
  {
    method: 'get',
    path: '/api/companymsservices',
    handler: getAllCompanyMSServices,
  },
  {
    method: 'get',
    path: '/api/companymsservices/:id/company',
    handler: getCompanyFromCompanyMSService,
  },
  {
    method: 'get',
    path: '/api/companymsservices/:id',
    handler: getCompanyMSService,
  },
  {
    method: 'post',
    path: '/api/companymsservices',
    handler: withSupplierProductAndItemGuard(postCompanyMSService),
  },
  {
    method: 'patch',
    path: '/api/companymsservices/:id',
    handler: withSupplierProductAndItemGuard(
      patchCompanyMSService,
      getCompanyMSServiceListData
    ),
  },
  {
    method: 'patch',
    path: '/api/companymsservices',
    handler: withSupplierProductAndItemGuard(
      patchCompanyMSServices,
      getCompanyMSServiceListData
    ),
  },
  {
    method: 'delete',
    path: '/api/companymsservices',
    handler: deleteCompanyMSServices,
  },
  {
    method: 'delete',
    path: '/api/companymsservices/:id',
    handler: deleteCompanyMSService,
  },
].map((h) => ({ ...h, ...{ handler: requireAuthenticatedUser(h.handler) } }))
